import { useEffect, useState } from 'react'
import { FormGrid, Button, Callout, Card, CardContent, CardHeader, LoadingState, TextField } from './ui'
import { useFeatureFlag } from '../lib/featureFlags'
import { priorityWeightsApi, type PriorityWeights } from '../api/workQueue'

/**
 * F-153 (TASK-0407, REQ-214-001): i pesi dei fattori di priorita' della coda di lavoro. Solo Admin (la pagina che lo
 * monta lo mostra solo a lui; il server risponde 403 a chiunque altro sulla scrittura) e solo con il flag
 * `coda.pesi_priorita`. Un peso a 0 spegne il fattore: con tutti a 0 il punteggio e' quello di sempre. Nessun importo.
 * Componenti M3.
 */
const PREFERRED: [keyof PriorityWeights['preferred'], string][] = [
  ['sectors', 'Settori preferiti (id reparto)'],
  ['operators', 'Operatori preferiti (id)'],
  ['products', 'Prodotti preferiti (id)'],
  ['segments', 'Segmenti cliente preferiti'],
]

export function PriorityWeightsPanel() {
  const enabled = useFeatureFlag('coda.pesi_priorita')
  const [data, setData] = useState<PriorityWeights | null>(null)
  const [weights, setWeights] = useState<Record<string, string>>({})
  const [lists, setLists] = useState<Record<string, string>>({})
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [saving, setSaving] = useState(false)

  const apply = (d: PriorityWeights) => {
    setData(d)
    setWeights(Object.fromEntries(d.factors.map(f => [f.key, String(f.weight)])))
    setLists(Object.fromEntries(PREFERRED.map(([k]) => [k, d.preferred[k].join(', ')])))
  }

  useEffect(() => {
    if (!enabled) return
    let alive = true
    priorityWeightsApi
      .get()
      .then(d => { if (alive) apply(d) })
      .catch(e => { if (alive) setError(e instanceof Error ? e.message : 'Errore') })
    return () => { alive = false }
  }, [enabled])

  if (!enabled) return null

  const max = data?.maxWeight ?? 100
  const invalid = (v: string | undefined) => {
    const n = Number(v)
    return !Number.isInteger(n) || n < 0 || n > max
  }
  const anyInvalid = data ? data.factors.some(f => invalid(weights[f.key])) : true

  const save = async () => {
    if (!data || anyInvalid) return
    setSaving(true)
    setError('')
    setMessage('')
    try {
      const updated = await priorityWeightsApi.update({
        weights: Object.fromEntries(data.factors.map(f => [f.key, Number(weights[f.key])])),
        preferred: Object.fromEntries(PREFERRED.map(([k]) => [k, lists[k] ?? ''])),
      })
      apply(updated)
      setMessage('Pesi salvati.')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card variant="outlined">
      <CardHeader title="Pesi di priorità della coda" />
      <CardContent>
        <div className="md-stack">
          <p className="md-typescale-body-medium text-muted m-0">
            Ogni fattore ha un peso da 0 a {max}; 0 lo spegne. Con tutti i pesi a 0 il punteggio è quello di sempre.
            {data ? ` ${data.formula}` : ''}
          </p>
          {error && <Callout tone="error" role="alert">{error}</Callout>}
          {message && <Callout tone="success" role="status">{message}</Callout>}
          {!data && !error && <LoadingState />}
          {data && (
            <form className="md-stack" onSubmit={e => { e.preventDefault(); void save() }}>
              <FormGrid>
                {data.factors.map(f => (
                  <div key={f.key}>
                    <TextField
                      id={`pw-${f.key}`}
                      label={f.label}
                      type="number"
                      min={0}
                      max={max}
                      step={1}
                      value={weights[f.key] ?? '0'}
                      onChange={e => setWeights(w => ({ ...w, [f.key]: e.target.value }))}
                      supportingText={f.description}
                      error={invalid(weights[f.key]) ? `Indica un intero da 0 a ${max}.` : undefined}
                    />
                  </div>
                ))}
              </FormGrid>
              <FormGrid>
                {PREFERRED.map(([k, label]) => (
                  <div key={k}>
                    <TextField
                      id={`pp-${k}`}
                      label={label}
                      type="text"
                      value={lists[k] ?? ''}
                      onChange={e => setLists(l => ({ ...l, [k]: e.target.value }))}
                      placeholder="valori separati da virgole"
                    />
                  </div>
                ))}
              </FormGrid>
              <div>
                <Button type="submit" variant="tonal" disabled={saving || anyInvalid}>
                  Salva i pesi
                </Button>
              </div>
            </form>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
